"use client";

import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import Link from "next/link";
import Image from "next/image";
import { Menu, X } from "lucide-react";
import { fsElement } from "@/lib/fs";

const mobileMenuZIndex = 9999;

export function MarketingHeader() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  const mobileMenu = (
    <>
      {/* Mobile menu overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/50 md:hidden"
          style={{ zIndex: mobileMenuZIndex }}
          aria-hidden
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Mobile menu drawer */}
      <div
        className={`fixed top-0 right-0 h-full w-72 max-w-[85vw] border-l border-border bg-card shadow-xl flex flex-col transition-transform duration-200 ease-out md:hidden ${
          mobileOpen ? "translate-x-0" : "translate-x-full"
        }`}
        style={{ zIndex: mobileMenuZIndex + 1 }}
        role="dialog"
        aria-modal="true"
        aria-label="Main menu"
      >
        <div className="p-4 border-b border-border flex items-center justify-between shrink-0 bg-card">
          <span className="font-semibold text-foreground">Menu</span>
          <button
            type="button"
            onClick={() => setMobileOpen(false)}
            className="p-2.5 rounded-lg text-muted-foreground hover:bg-muted/80 hover:text-foreground transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
            aria-label="Close menu"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <nav className="flex-1 flex flex-col p-4 gap-1 overflow-y-auto min-h-0 bg-card">
          <Link
            href="/"
            onClick={() => setMobileOpen(false)}
            className="px-4 py-3 rounded-lg text-foreground hover:bg-muted transition-colors font-medium"
          >
            Home
          </Link>
          <Link
            href="/features"
            onClick={() => setMobileOpen(false)}
            className="px-4 py-3 rounded-lg text-foreground hover:bg-muted transition-colors font-medium"
          >
            Features
          </Link>
          <Link
            href="/pricing"
            onClick={() => setMobileOpen(false)}
            className="px-4 py-3 rounded-lg text-foreground hover:bg-muted transition-colors font-medium"
          >
            Pricing
          </Link>
          <Link
            href="/faq"
            onClick={() => setMobileOpen(false)}
            className="px-4 py-3 rounded-lg text-foreground hover:bg-muted transition-colors font-medium"
          >
            FAQ
          </Link>
          <Link
            href="/contact"
            onClick={() => setMobileOpen(false)}
            className="px-4 py-3 rounded-lg text-foreground hover:bg-muted transition-colors font-medium"
          >
            Contact
          </Link>
          <Link
            href="/help"
            onClick={() => setMobileOpen(false)}
            className="px-4 py-3 rounded-lg text-foreground hover:bg-muted transition-colors font-medium"
          >
            Help
          </Link>
          <div className="border-t border-border my-2" />
          <Link
            href="/auth/login"
            onClick={() => setMobileOpen(false)}
            className="px-4 py-3 rounded-lg border-2 border-border text-foreground hover:bg-muted transition-colors font-medium text-center"
            {...fsElement("Header Login Link", { id: "header-login-mobile", section: "header-mobile" })}
          >
            Login
          </Link>
          <Link
            href="/auth/signup"
            onClick={() => setMobileOpen(false)}
            className="px-4 py-3 rounded-lg bg-primary text-primary-foreground hover:opacity-95 transition-opacity font-medium text-center"
            {...fsElement("Header Get Started Link", { id: "header-get-started-mobile", section: "header-mobile" })}
          >
            Get started
          </Link>
        </nav>
      </div>
    </>
  );

  return (
    <header className="sticky top-0 z-50 border-b border-border bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/80">
      <div className="container mx-auto px-4 py-3 flex items-center justify-between">
        <Link href="/" className="flex flex-col items-center gap-1 hover:opacity-90 transition-opacity min-w-0">
          <Image
            src="/logo192.png"
            alt=""
            width={28}
            height={28}
            className="rounded shrink-0"
            aria-hidden
          />
          <span className="text-xs font-semibold text-foreground tracking-tight">Book4</span>
        </Link>
        <nav className="flex items-center gap-2 sm:gap-4">
          <div className="hidden md:flex items-center gap-6">
            <Link href="/" className="text-sm text-foreground hover:text-primary transition-colors">
              Home
            </Link>
            <Link href="/features" className="text-sm text-foreground hover:text-primary transition-colors">
              Features
            </Link>
            <Link href="/pricing" className="text-sm text-foreground hover:text-primary transition-colors">
              Pricing
            </Link>
            <div className="relative group">
              <Link href="/faq" className="text-sm text-foreground hover:text-primary transition-colors flex items-center gap-1">
                Resources
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </Link>
              <div className="absolute top-full left-0 mt-2 w-48 bg-card border border-border rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                <div className="py-2">
                  <Link href="/faq" className="block px-4 py-2 text-sm text-foreground hover:bg-muted transition-colors">
                    FAQ
                  </Link>
                  <Link href="/contact" className="block px-4 py-2 text-sm text-foreground hover:bg-muted transition-colors">
                    Contact
                  </Link>
                  <Link href="/help" className="block px-4 py-2 text-sm text-foreground hover:bg-muted transition-colors">
                    Help
                  </Link>
                </div>
              </div>
            </div>
          </div>

          {/* Mobile menu button */}
          <button
            type="button"
            onClick={() => setMobileOpen(true)}
            className="md:hidden p-2.5 -mr-2 rounded-lg text-muted-foreground hover:bg-muted/80 hover:text-foreground transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
            aria-label="Open menu"
            aria-expanded={mobileOpen}
          >
            <Menu className="w-6 h-6" />
          </button>

          <div className="hidden md:flex items-center gap-4">
            <Link
              href="/auth/login"
              className="text-sm font-medium rounded-lg border-2 border-border bg-background px-5 py-2 hover:bg-muted/80 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-foreground"
              {...fsElement("Header Login Link", { id: "header-login", section: "header" })}
            >
              Login
            </Link>
            <Link
              href="/auth/signup"
              className="text-sm bg-primary text-primary-foreground px-5 py-2 rounded-lg font-medium hover:opacity-95 transition-opacity shadow-sm"
              {...fsElement("Header Get Started Link", { id: "header-get-started", section: "header" })}
            >
              Get started
            </Link>
          </div>
        </nav>
      </div>

      {mounted && typeof document !== "undefined" && createPortal(mobileMenu, document.body)}
    </header>
  );
}
